import {
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useDispatch, batch, useSelector } from 'react-redux';
import {
  Conversation,
  User,
  Participant,
  addParticipant,
  ConnectionState,
  setConnectionState,
  updateParticipant,
  addMetadata,
  Message,
  addMessages,
  updateMetadata,
  removeMetadata,
  selectTwilioClient,
  setJwtToken,
  editGroupParticipants,
  setChatAlert,
  ChatAlert,
  playNotificationSound,
  ConversationAttributes,
  ConversationType,
  updateReachabilityStatus,
  TwilioUserAttributes,
} from 'models/twilio';
import { toTwilioUser } from 'models/twilio/converters';
import { getTwilioJwt } from 'network/twilioRequests';
import useCurrentAttendee from './useCurrentAttendee';
import buildInitialMetadata from '../utils/buildInitialMetadata';
import useFirebaseChatConfig from './useFirebaseChatConfig';
import useTwilioVideoCall from './useTwilioVideoCall';
import useMutableRef from './useMutableRef';
import ConverastionContext from '../ConversationContext';
import TRANSLATIONS from '../translations';

const chatAlertParamMap: Record<ConnectionState, { translation: readonly [string, string], severity: ChatAlert['severity'] }> = {
  [ConnectionState.CONNECTING]: {
    translation: TRANSLATIONS.STATE.CONNECTING,
    severity: 'info',
  },
  [ConnectionState.CONNECTED]: {
    translation: TRANSLATIONS.STATE.CONNECTED,
    severity: 'success',
  },
  [ConnectionState.DISCONNECTING]: {
    translation: TRANSLATIONS.STATE.DISCONNECTING,
    severity: 'info',
  },
  [ConnectionState.DISCONNECTED]: {
    translation: TRANSLATIONS.STATE.DISCONNECTED,
    severity: 'info',
  },
  [ConnectionState.DENIED]: {
    translation: TRANSLATIONS.STATE.DENIED,
    severity: 'error',
  },
};

const useTwilioEventHandler = () => {
  const { announcementEnabled, boothChatroomDisabled } = useFirebaseChatConfig();
  const dispatch = useDispatch();
  const {
    addConversation,
    removeConversationById,
    processing,
    isHandlerAttached,
    recordHandlerAttachment,
  } = useContext(ConverastionContext);
  const clientRef = useMutableRef(useSelector(selectTwilioClient));
  const videoCallHandlerRef = useMutableRef(useTwilioVideoCall());
  const attendeeRef = useMutableRef(useCurrentAttendee());

  const attachConversationEventHandlers = useCallback((conversation: Conversation) => {
    const conversationType = (conversation.attributes as ConversationAttributes)?.type;

    // Simply the NOT of boothChatroomDisabled && conversationType === ConversationType.GROUP
    if (!boothChatroomDisabled || conversationType !== ConversationType.GROUP) {
      conversation.on('messageAdded', (message: Message) => {
        const fromSelf = attendeeRef.current.isCurrentAttendee(message.author);
        batch(() => {
          if (!fromSelf) dispatch(playNotificationSound(conversationType));
          dispatch(addMessages({
            conversationId: conversation.sid,
            messages: [message],
            updateLastMessage: true,
            fromSelf,
          }));
        });
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    conversation.on('updated', ({ conversation, updateReasons }: { conversation: Conversation, updateReasons: Conversation.UpdateReason[] }) => {
      if (updateReasons.includes('lastReadMessageIndex')) {
        dispatch(updateMetadata({
          id: conversation.sid,
          lastReadMessageIndex: conversation.lastReadMessageIndex,
        }));
      }
    });
    if ([ConversationType.GROUP, ConversationType.PUBLIC].includes(conversationType)) {
      conversation.on('participantJoined', (participant: Participant) => {
        participant.getUser();
        dispatch(editGroupParticipants({
          type: 'ADD',
          conversationId: conversation.sid,
          participantIds: [participant.identity],
        }));
      });
      conversation.on('participantLeft', (participant: Participant) => {
        dispatch(editGroupParticipants({
          type: 'REMOVE',
          conversationId: conversation.sid,
          participantIds: [participant.identity],
        }));
      });
    }
  }, [boothChatroomDisabled, dispatch, attendeeRef]);

  const connectionStateChangedHandler = useCallback((connectionState: ConnectionState) => {
    const { translation, severity } = chatAlertParamMap[connectionState];

    batch(() => {
      dispatch(setConnectionState(connectionState));
      dispatch(setChatAlert({
        message: translation[1],
        translationKey: translation[0],
        severity,
      }));
    });
  }, [dispatch]);

  const conversationJoinedHandler = useCallback(async (conversation: Conversation) => {
    const initialMetadata = await buildInitialMetadata(conversation, attendeeRef.current.uuid);
    const { items: messages } = await conversation.getMessages(1);

    // Do not subscribe to Announcement if it is disabled.
    if (initialMetadata.conversationType === ConversationType.ANNOUNCEMENT && !announcementEnabled) return;

    // This is to stop multiple event handlers to be attached to the same conversation.
    if (isHandlerAttached(conversation.sid)) return;

    processing.add(conversation.sid, initialMetadata.conversationType);
    attachConversationEventHandlers(conversation);
    recordHandlerAttachment(conversation.sid);
    addConversation(conversation, initialMetadata);
    batch(() => {
      dispatch(setConnectionState(ConnectionState.CONNECTED));
      dispatch(addMetadata(initialMetadata));
      // addMessages has a getUsers side effect
      // This is to display the user's name in the message preview
      if (messages.length) dispatch(addMessages({ conversationId: conversation.sid, messages }));
    });
    processing.remove(conversation.sid, initialMetadata.conversationType);
  }, [
    announcementEnabled,
    attendeeRef,
    addConversation,
    attachConversationEventHandlers,
    dispatch,
    isHandlerAttached,
    recordHandlerAttachment,
    processing,
  ]);

  const conversationRemovedHandler = useCallback((conversation: Conversation) => {
    removeConversationById(conversation.sid);
    dispatch(removeMetadata(conversation.sid));
  }, [dispatch, removeConversationById]);

  const converastionUpdatedHandler = useCallback(({
    conversation,
    updateReasons,
  }: {
    conversation: Conversation,
    updateReasons: Conversation.UpdateReason,
  }) => {
    if (updateReasons.includes('attributes')) {
      videoCallHandlerRef.current.onAttributeUpdatedHandler(conversation);
    }
  }, [videoCallHandlerRef]);

  const userSubscribedHandler = useCallback(async (user: User) => {
    if (!user.identity) return;
    const isCurrentUser = attendeeRef.current.isCurrentAttendee(user.identity);
    batch(() => {
      dispatch(setConnectionState(ConnectionState.CONNECTED));
      dispatch(addParticipant(toTwilioUser(user, isCurrentUser)));
    });
  }, [dispatch, attendeeRef]);

  const userUpdatedHandler = useCallback(({
    user,
    updateReasons,
  }: {
    user: User,
    updateReasons: User.UpdateReason[],
  }) => {
    if (!user.identity) return;
    const { identity, friendlyName, isOnline } = user;
    const attributes = user.attributes as TwilioUserAttributes;
    const isCurrentUser = attendeeRef.current.isCurrentAttendee(identity);
    batch(() => {
      if (updateReasons.includes('reachabilityOnline')) {
        dispatch(updateReachabilityStatus({
          id: identity,
          isOnline,
          isIdle: false,
        }));
      }
      if (updateReasons.includes('attributes')) {
        dispatch(updateParticipant({
          id: identity,
          tag: attributes?.userTag ?? null,
          description: attributes?.userDescription ?? null,
          exhibitorBoothId: attributes?.boothId ?? null,
          email: attributes?.email ?? null,
          role: attributes?.role ?? 'attendee',
          isSelf: isCurrentUser,
        }));
        dispatch(updateReachabilityStatus({
          id: identity,
          isOnline,
          isIdle: attributes?.isIdle ?? false,
        }));
      }
      if (updateReasons.includes('friendlyName')) {
        dispatch(updateParticipant({
          id: identity,
          name: friendlyName,
          isSelf: isCurrentUser,
        }));
      }
    });
  }, [dispatch, attendeeRef]);

  const updateTokenHandler = useCallback(async () => {
    if (!clientRef.current) return;
    const jwtToken = await getTwilioJwt(attendeeRef.current.uuid);
    dispatch(setJwtToken(jwtToken));
    clientRef.current.updateToken(jwtToken);
  }, [dispatch, clientRef, attendeeRef]);

  const connectionErrorHandler = useCallback(({ message }) => {
    if (message) dispatch(setChatAlert({ message, severity: 'error' }));
    else dispatch(setChatAlert({ message: TRANSLATIONS.ERRORS.CONNECTION[1], translationKey: TRANSLATIONS.ERRORS.CONNECTION[0], severity: 'error' }));
  }, [dispatch]);

  const handlers = useMemo(() => ({
    attachConversationEventHandlers,
    connectionStateChangedHandler,
    conversationJoinedHandler,
    userSubscribedHandler,
    userUpdatedHandler,
    converastionUpdatedHandler,
    conversationRemovedHandler,
    updateTokenHandler,
    connectionErrorHandler,
  }), [
    attachConversationEventHandlers,
    connectionStateChangedHandler,
    conversationJoinedHandler,
    userSubscribedHandler,
    userUpdatedHandler,
    converastionUpdatedHandler,
    conversationRemovedHandler,
    updateTokenHandler,
    connectionErrorHandler,
  ]);

  return handlers;
};

export default useTwilioEventHandler;
